import * as React from 'react';
import * as types from '../../../types';
import * as services from '../../../services';
import { IGoMiscFile, GoMiscFile } from '../../../types';
import { CrTextField } from '../../cr/CrTextField';
import { FieldErrorMessage } from '../../cr/FieldDecorators';
import { FormButtons } from '../../cr/FormButtons';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { FormCommandBar } from '../../cr/FormCommandBar';
import styles from '../../../styles/cr.module.scss';
import { AppIDs, getAppKeyByValue } from '../../../AppGlobals';

export interface IMiscFileSaveFormProps extends types.IBaseComponentProps {
    miscFileID: number;
    showForm: boolean;
    onSaved?: () => void;
    onCancelled?: () => void;
}

export interface IErrorMessage {
    Details: string;
    FileUpload: string;
}
export class ErrorMessage implements IErrorMessage {
    public Details = null;
    public FileUpload = null;
}

export interface IMiscFileSaveFormState {
    Loading: boolean;
    FormData: IGoMiscFile;
    FormDataBeforeChanges: IGoMiscFile;
    FormIsDirty: boolean;
    UploadStatus: string;
    UploadProgress: number;
    ShowUploadProgress: boolean;
    ShowFileUpload: boolean;
    ErrMessages: IErrorMessage;
}

export class MiscFileSaveFormState implements IMiscFileSaveFormState {
    public Loading = false;
    public FormData = new GoMiscFile();
    public FormDataBeforeChanges = new GoMiscFile();
    public FormIsDirty = false;
    public UploadStatus = "";
    public UploadProgress: number = 0;
    public ShowUploadProgress = false;
    public ShowFileUpload = false;
    public ErrMessages = new ErrorMessage();
}

export default class MiscFileSaveForm extends React.Component<IMiscFileSaveFormProps, IMiscFileSaveFormState> {

    private goMiscFileService: services.GoMiscFileService = new services.GoMiscFileService();
    private zFileService: services.ZFileService = new services.ZFileService();

    constructor(props: IMiscFileSaveFormProps, state: IMiscFileSaveFormState) {
        super(props);
        this.state = new MiscFileSaveFormState();
    }

    public render(): React.ReactElement<IMiscFileSaveFormProps> {
        return (
            <Panel isOpen={this.props.showForm} headerText={"Misc File"} type={PanelType.medium} onRenderNavigation={() => <FormCommandBar onSave={this.saveMiscFile} onCancel={this.props.onCancelled} /*saveDisabled={this.state.ShowUploadProgress}*/ />}>
                <div className={styles.cr}>
                    {this.renderFormFields()}
                    <FormButtons
                        primaryText={"Save"}
                        primaryDisabled={this.state.ShowUploadProgress}
                        onPrimaryClick={() => this.saveMiscFile()}
                        onSecondaryClick={this.props.onCancelled}
                    />
                </div>
            </Panel>
        );
    }

    public renderFormFields() {
        return (
            <React.Fragment>
                {this.renderMiscFileDetails()}
                {this.renderFileUpload()}
            </React.Fragment>
        );
    }

    private renderMiscFileDetails() {
        return (
            <CrTextField
                label="Misc File Details"
                required={true}
                className={styles.formField}
                value={this.state.FormData.Details}
                onChange={(_, v) => this.changeTextField(v, "Details")}
                multiline={true}
                rows={3}
                errorMessage={this.state.ErrMessages.Details}
            />
        );
    }

    private renderFileUpload() {
        if (this.state.ShowFileUpload == false)
            return null;

        return (
            <div style={{ marginBottom: '10px' }}>
                <div>
                    <input type="file" name="fileUpload" id="fileUpload"></input>
                    {this.state.ErrMessages.FileUpload && <FieldErrorMessage value={this.state.ErrMessages.FileUpload} />}
                </div>
                {this.state.ShowUploadProgress && <div style={{ minHeight: '80px', marginTop: '15px' }}>
                    <div>
                        {this.state.UploadStatus}
                    </div>
                    <div>
                        {this.state.UploadProgress} %
                    </div>
                </div>}
            </div>
        );
    }

    private uploadFile = async (miscFileID: number, uploadedByUserId: number) => {
        this.setState({
            UploadStatus: "Uploading file ...",
            ShowUploadProgress: true
        });

        let myfile = (document.querySelector("#fileUpload") as HTMLInputElement).files[0];
        let fileName: string = myfile.name;

        try {
            const appShortCode = getAppKeyByValue(AppIDs.GO);
            const response = await this.zFileService.uploadFile(appShortCode, myfile, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                this.setState({
                    UploadProgress: progress,
                    UploadStatus: progress === 100 ? "Processing file..." : "Uploading to server..."
                });
            });

            if (response.status === true) {
                this.afterFileUpload(String(response.fileID), miscFileID, fileName, uploadedByUserId);
            }
            else {
                console.log('error in uploading file');
            }
        } catch (error) {
            console.error('Error uploading file', error);
        }
    }

    private afterFileUpload = (fileUniqueID: string, miscFileID: number, fileName: string, uploadedByUserId: number): void => {
        const fdata = { ...this.state.FormData, "FileUniqueID": fileUniqueID, "Title": fileName, "ID": miscFileID, "UploadedByUserId": uploadedByUserId };
        this.setState({
            FormData: fdata
        }, this.saveMiscFile);
    }

    private saveMiscFile = (): void => {
        if (this.validateEntity()) {
            if (this.props.errorHandling?.onError) this.props.errorHandling?.onError(null);

            let f: IGoMiscFile = { ...this.state.FormData };

            //remove all the child and parent entities before sending post/patch
            delete f.User; //parent entity
            if (f.ID === 0) {
                //firts create record in the db, so we can get the ID, then use the ID to append in the file name to make file name unique
                this.goMiscFileService.create(f).then(x => {
                    this.uploadFile(x.ID, x.UploadedByUserId);
                });
            }
            else {
                this.goMiscFileService.updatePut(f.ID, f).then(this.props.onSaved, (err) => {
                    if (this.props.errorHandling?.onError) this.props.errorHandling?.onError(`Error updating item`, err.message);
                });
            }
        }
    }

    private validateEntity = (): boolean => {
        let returnVal: boolean = true;
        let errMsg: IErrorMessage = { ...this.state.ErrMessages };

        if ((this.state.FormData.Details === null) || (this.state.FormData.Details === '')) {
            errMsg.Details = "Details required";
            returnVal = false;
        }
        else {
            errMsg.Details = null;
        }
        if (this.state.ShowFileUpload === true && ((document.querySelector("#fileUpload") as HTMLInputElement).files[0]) == null) {
            errMsg.FileUpload = "File required";
            returnVal = false;
        }
        else {
            errMsg.FileUpload = null;
        }

        //at the end set state
        this.setState({ ErrMessages: errMsg });
        return returnVal;
    }

    private loadMiscFile = (): Promise<void> => {
        let x = this.goMiscFileService.read(this.props.miscFileID).then((p: IGoMiscFile): void => {
            this.setState({
                FormData: p,
                FormDataBeforeChanges: p,
            });

        }, (err) => { if (this.props.errorHandling?.onError) this.props.errorHandling?.onError(`Error loading misc file data`, err.message); });
        return x;
    }

    public componentDidMount(): void {
        this.setState({ Loading: true });
        let loadingPromises = [this.loadLookups()];
        if (this.props.miscFileID) {
            loadingPromises.push(this.loadMiscFile());
        }
        else {
            this.setState({ ShowFileUpload: true });
        }
        Promise.all(loadingPromises).then(p => this.onAfterLoad(p[1])).then(p => this.setState({ Loading: false })).catch(err => this.setState({ Loading: false }));
    }

    private loadLookups(): Promise<any> {
        let proms: any[] = [];
        return Promise.all(proms);
    }

    private onAfterLoad = (entity: types.IEntity): void => {
    }

    private changeTextField = (value: string, f: string): void => {
        this.setState({ FormData: this.cloneObject(this.state.FormData, f, value), FormIsDirty: true });
    }

    private cloneObject(obj, changeProp?, changeValue?) {
        if (changeProp)
            return { ...obj, [changeProp]: changeValue };
        return { ...obj };
    }
}